import React from 'react'
import papa from 'papaparse'
import {
  useClearanceStatus,
  ClearanceStatusType,
  useDownloadClearanceStatus,
  GetClearanceStatus_getClearanceStatus_users,
  getFormattedDateAndTime,
  Me_me_User_GroupsWithRole,
  Groups_groups_Groups,
} from '@modmd/data'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { Spinner } from 'components/Spinner'
import { COLOR } from 'theme'
import { ClearanceStatusLabels, VaccineStatusLabels, TestsStatusLabels } from 'utils/labels'

interface Props {
  searchState: {
    data: {
      createdAt: string
      nameOrClearanceIdLike: string
      page?: string
    }
  }
  group: Me_me_User_GroupsWithRole | Groups_groups_Groups
}

export const DownloadClearanceButton = ({ searchState, group }: Props) => {
  const [download, setDownload] = React.useState(false)
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const { data: clearanceStatus } = useClearanceStatus({
    filter: Object.entries({
      createdAt: searchState.data.createdAt,
      groupIds: [group ? group.id : ''],
      nameOrClearanceIdLike: searchState.data.nameOrClearanceIdLike,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {
      groupIds: [group ? group.id : ''],
    }),
    page: Number(searchState.data.page),
  })

  const getStatus = (
    entranceResponsible: string | null,
    entranceStatus: string | null,
    clearanceCheckStatus: string | null
  ) => {
    if (entranceResponsible)
      return ClearanceStatusLabels[entranceStatus as keyof typeof ClearanceStatusLabels]
    if (clearanceCheckStatus === ClearanceStatusType.Cleared) return 'Auto approved'
    if (clearanceCheckStatus === ClearanceStatusType.NotCleared) return 'Auto denied'
    return ClearanceStatusLabels[clearanceCheckStatus as keyof typeof ClearanceStatusLabels]
  }

  const [
    downloadClearanceStatuses,
    { data: clearanceStatusesCsv, loading: isDownloadingClearanceStatuses },
  ] = useDownloadClearanceStatus({
    onCompleted: () => {
      if (download) {
        const users = clearanceStatusesCsv?.getClearanceStatus?.users?.map(
          (user: GetClearanceStatus_getClearanceStatus_users) => {
            const {
              clearanceId,
              firstName,
              lastName,
              testCheckedStatus,
              vaccinationStatus,
              clearanceCheckStatus,
              entranceStatus,
              entranceResponsible,
              dateOfClearance,
            } = user
            const fullName = `${firstName || ''} ${lastName || ''}`
            const testStatus =
              TestsStatusLabels[testCheckedStatus as keyof typeof TestsStatusLabels]
            const vaccStatus =
              VaccineStatusLabels[vaccinationStatus as keyof typeof VaccineStatusLabels]
            const status = getStatus(entranceResponsible, entranceStatus, clearanceCheckStatus)

            const date = getFormattedDateAndTime(dateOfClearance, true)

            const users = {
              clearanceId,
              fullName,
              testStatus,
              vaccStatus,
              status,
              date,
            }
            return users
          }
        )
        if (users && !downloadableDataUrl) {
          const csv = papa.unparse(users, { header: true })
          const dataBlob = new Blob([csv], { type: 'text/csv' })

          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(dataBlob)
          link.setAttribute(
            'download',
            `Fastpass_${group?.name}-${getFormattedDateAndTime(searchState.data.createdAt)}.csv`
          )
          document.body.appendChild(link)
          link.click()
          setDownload(false)
        } else if (downloadableDataUrl) {
          window.URL.revokeObjectURL(downloadableDataUrl)
          setDownloadableDataUrl('')
          setDownload(false)
        }
      }
    },
  })

  const handleDownloadClearance = () => {
    downloadClearanceStatuses({
      variables: {
        filterData: {
          createdAt: searchState.data.createdAt,
          groupIds: [group ? group.id : ''],
          nameOrClearanceIdLike: searchState.data.nameOrClearanceIdLike,
        },
        pagination: {
          from: 0,
          length: Number(clearanceStatus?.getClearanceStatus.pagination.total),
        },
      },
    })
    setDownload(true)
  }

  return (
    <Button
      onClick={handleDownloadClearance}
      appearance="link"
      size="large"
      disabled={isDownloadingClearanceStatuses}
    >
      {isDownloadingClearanceStatuses ? (
        <Spinner size="small" color={COLOR.brand} />
      ) : (
        <Icon.CloudDownloadOutline size="M" />
      )}
    </Button>
  )
}
